// Live-preview engine input for O-2783 "pool over offers".
//
// In Pool mode the editor preview must compute the same pool split the server
// does. We mirror V3OfferPool#offer_deal_overlay + V3OfferPool#member_json
// exactly: the pool's shared deal terms REPLACE the active offer's deal, the
// `pool_mode` flag is set, and `pool_members` is the member list the engine
// splits the pool across.
//
// In Independent mode the preview must be byte-for-byte what it was before this
// feature existed: we return the active offer untouched (no pool_mode, no
// pool_members), so FinancialSummaryMath behaves exactly as it did for a
// single-artist offer. This is the load-bearing guarantee the spec calls out.

// Coerce a form/stored value to a number or null. null is meaningful — the
// engine treats a null member percentage as "inherit the offer-level upside"
// (FinancialSummaryMath.core.js), distinct from 0 (force zero) — so empty,
// nullish, and non-numeric values must all become null, never 0 or NaN. Shared
// by the pool deal + member-share inputs (single source of truth).
export const numOrNull = (value) => {
  if (value === '' || value === null || value === undefined) return null;
  const n = Number(value);
  return Number.isNaN(n) ? null : n;
};

export const poolMemberKey = (member) => (
  member.performer_id != null ? member.performer_id : (member.id != null ? -member.id : member.id)
);

// Build a single `pool_members` entry mirroring the server's member_json shape.
// `member` is the active offer's lineup row; `activeOffer` is the live (locally
// edited) active offer whose pool-share fields we overlay onto its own row so
// the preview reflects in-flight keystrokes. `performerOverride` lets the
// active row carry the offer's real performer_id (lineup rows don't serialize
// one).
const buildMember = (member, { activeOffer, isActive }) => {
  // The active member's editable pool-share fields come from the live offer
  // (so unsaved edits show immediately); other members fall back to whatever
  // the lineup row carries.
  const source = isActive ? activeOffer : member;

  // The engine keys members on performerId, and V3PoolSplitPreview looks each
  // take up by the lineup row's `performer_id` (= primary_performer.id). Derive
  // the key from the lineup row for EVERY member — including the active one.
  // The active offer's own `performer_id` is the legacy `performer` column (a
  // different Performer record than primary_performer), so keying the active
  // member off it filed its take under an id the preview never looks up, which
  // showed the active artist as $0 (O-2783). Lineup rows always carry a
  // performer_id; the offer id is only a last-resort fallback.
  const performerId = poolMemberKey(member);

  return {
    performer_id: performerId,
    performer_name: member.performer_name,
    position: member.position,
    // `source`, not `member`: for the ACTIVE member this is the live offer, so a
    // guarantee being typed on the pool card moves the split preview as you type,
    // like every other field here. Reading the lineup row instead left the
    // preview showing a balanced pool while the real split was overdrawn.
    guarantee: numOrNull(source.dollar_amount),
    percentage: numOrNull(source.pool_percentage),
    dollar_or_percentage_operator: source.dollar_or_percentage_operator,
    artist_payout_cap: numOrNull(source.artist_payout_cap),
    artist_payout_floor: numOrNull(source.artist_payout_floor),
    payout_strategy: source.pool_payout_strategy,
    // O-2783 R6: per-member tax withholding so a member taxed differently than the
    // headliner settles right; the engine (memberTaxWithholding) reads these per
    // member and falls back to the offer-level values when null. Per-member
    // breakeven is intentionally NOT carried — in Pool mode the breakeven gates the
    // shared pool deal, not each member's share.
    has_tax_withholding: source.has_tax_withholding,
    tax_withholding_percentage: numOrNull(source.tax_withholding_percentage),
    tax_withholding_minimum_threshold: numOrNull(source.tax_withholding_minimum_threshold),
  };
};

// Build the `pool_members` array from the active offer's lineup.
//
// Only rows that are actually IN this pool. The lineup is grouped by
// calendar_event, so it can contain an offer that shares the event without
// sharing the pool — a copy taken with "keep event" is precisely that. Mapping
// those in put a phantom member in the preview carrying the original's
// guarantee, which consumed the whole leftover and showed every real member $0,
// while the server's pool-keyed member_json settled correctly. The two producers
// have to agree on who is in the pool.
export const buildPoolMembers = (activeOffer, lineup) => {
  const poolId = activeOffer?.v3_offer_pool_id ?? null;
  const rows = (lineup || []).filter((member) => (
    // A row with no pool id at all predates this field; keep the old behaviour
    // rather than silently emptying the preview.
    member.v3_offer_pool_id === undefined
      || member.v3_offer_pool_id === poolId
      || member.id === activeOffer?.id
  ));
  return rows.map((member) => buildMember(member, {
    activeOffer,
    isActive: member.id === activeOffer?.id,
  }));
};

// Whether the editor should render the pool split preview for this offer.
// True whenever the pool overlay exists and is in Pool mode — including a
// single-artist lineup, so a booker can configure the pool deal and this
// artist's share while building the lineup one artist at a time (O-2783). The
// engine handles a 1-member pool (isSingleArtistMode → the lone member takes
// the whole pool when their strategy is remainder/unset).
export const isPoolPreviewActive = (activeOffer, lineup) => (
  (activeOffer?.v3_offer_pool?.mode === 'pool')
  && (lineup || []).length >= 1
);

// The active offer's own member entry in the engine's poolMembers output. The
// engine keys each member on the lineup row's performer_id (= primary_performer.id),
// so match on the active offer's primary_performer.id. Returns null when the
// member isn't found (e.g. before the first split computes, or a degenerate
// offer with no primary_performer).
export const activeMember = (activeOffer, poolMembers) => {
  const performerId = poolMemberKey({ id: activeOffer?.id, performer_id: activeOffer?.primary_performer?.id });
  if (performerId == null) return null;
  return (poolMembers || []).find((m) => m.performerId === performerId) || null;
};

// The active artist's POST-TAX pool earnings — the value the financial summary's
// "Potential Artist Earnings" shows in Pool mode. Mirrors the single-offer
// meaning (artistEarnings.artistSettlementDue = net of tax withholding), NOT the
// engine's pool-overwritten artistSettlementDue (which is the SUM across all
// members). Falls back to the pre-tax take when settlementTake is absent.
export const activeMemberSettlementDue = (activeOffer, poolMembers) => {
  const member = activeMember(activeOffer, poolMembers);
  if (!member) return 0;
  return member.settlementTake != null ? member.settlementTake : member.take;
};

// Build the engineOffer FinancialSummaryMath consumes. In Independent mode (or
// when there is no pool) the active offer is returned UNCHANGED so the preview
// is identical to the pre-pool behavior.
export const buildPoolEngineOffer = (activeOffer, lineup) => {
  if (!activeOffer) return activeOffer;
  if (!isPoolPreviewActive(activeOffer, lineup)) return activeOffer;

  const pool = activeOffer.v3_offer_pool;

  return {
    ...activeOffer,
    pool_mode: true,
    dollar_amount: pool.dollar_amount,
    upside_percentage: pool.upside_percentage,
    dollar_or_percentage_operator: pool.dollar_or_percentage_operator,
    from_dollar_one: pool.from_dollar_one,
    revenue_base: pool.revenue_base,
    breakeven_threshold: pool.breakeven_threshold,
    artist_payout_cap: pool.pool_payout_cap,
    artist_payout_floor: pool.pool_payout_floor,
    pool_members: buildPoolMembers(activeOffer, lineup),
  };
};
